import {
    Injectable, inject
} from "@angular/core";
import {
    FileRequestSubject,
    FileResponse,
    RequestUtil
} from "@shopliftr/common-js/shared";
import {
    AdAsset, AdAssetListOptions
} from "@shopliftr/common-js/smart";
import { MimeType } from "@shopliftr/common-js/data";
import {
    Observable,
    map
} from "rxjs";

import { IInternalApiFile } from "../../../../interfaces/internal-api-file";
import { InternalApiFileRequestService } from "../../request/internal-api/internal-api-file-request.service";

/**
 * API File Service for AdAsset file operations.
 *
 * Handles file management operations (list, upload, delete) for ad assets,
 * delegating HTTP communication to InternalApiFileRequestService.
 *
 * @pattern API File Service / AdAsset File Management
 * @providedIn root
 */
@Injectable({
    providedIn: "root"
})
export class AdAssetApiFileService implements IInternalApiFile {

    readonly fileRequestSubject = FileRequestSubject.AdAsset;

    /**
     * Handles HTTP communication with Internal API for file operations.
     */
    private readonly _internalApiFileRequestService = inject(InternalApiFileRequestService);

    /**
     * Lists ad assets based on filter options.
     *
     * @param options - Filter criteria for listing assets
     * @returns Observable that emits an array of AdAsset entities
     */
    list$(options: AdAssetListOptions): Observable<Array<AdAsset>> {

        const request = RequestUtil.createFileListRequest(FileRequestSubject.AdAsset, options);

        return this._internalApiFileRequestService.listFiles$(request).pipe(map((response) => {

            return response.results.map((result) => {

                const asset = new AdAsset();
                asset.setDefaults();
                asset.fullPath = result.filePath;
                return asset;
            });
        }));
    }

    /**
     * Deletes a single ad asset at the specified path.
     *
     * @param fullPath - Full path to the asset to delete
     * @returns Observable that emits the file deletion response
     */
    delete$(fullPath: string): Observable<FileResponse> {

        return this.bulkDelete$([fullPath]);
    }

    /**
     * Deletes multiple ad assets at the specified paths.
     *
     * @param fullPaths - Array of full paths to assets to delete
     * @returns Observable that emits the file deletion response
     */
    bulkDelete$(fullPaths: Array<string>): Observable<FileResponse> {

        const request = RequestUtil.createFileDeleteRequest(FileRequestSubject.AdAsset, fullPaths);
        return this._internalApiFileRequestService.deleteFiles$(request);
    }

    /**
     * Uploads an ad asset file to Internal API.
     *
     * @param asset - AdAsset entity containing the target path
     * @param file - File blob to upload
     * @param fileType - MIME type of the file
     * @returns Observable that emits the file upload response
     */
    upload$(asset: AdAsset, file: Blob, fileType: MimeType): Observable<FileResponse> {

        const request = RequestUtil.createFileUploadRequest(FileRequestSubject.AdAsset, asset.fullPath, fileType);
        return this._internalApiFileRequestService.uploadFile$(request, file);
    }
}
